"use client"

import { useState, type ReactNode } from "react"
import { useSearchParams } from "next/navigation"
import { Loader2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Card } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { OtherMessagesTab } from "./other-messages-tab"
import { SystemEventRow } from "./system-event-row"
import { TenantNotificationsTab } from "./tenant-notifications-tab"
import { useSystemNotificationSettingsQuery } from "@/hooks/queries/system-notifications"
import { useTranslations } from "next-intl"


const TABS = ["platform", "tenant", "other"] as const
type SystemNotificationsTab = (typeof TABS)[number]

/**
 * Platform events: which events about tenants reach the operator, and through which of the platform
 * tenant's own channels. Tenant notifications: which notifications tenants receive at all. Other
 * messages: what the platform sends outside the catalog.
 */
export function SystemNotificationsPage() {
  const tUi = useTranslations("SystemNotifications.SystemNotificationsSystemNotificationsPage")

  const searchParams = useSearchParams()
  const [tab, setTab] = useState<SystemNotificationsTab>(parseTab(searchParams.get("tab")))

  function changeTab(value: string) {
    const next = parseTab(value)
    setTab(next)
    const url = new URL(window.location.href)
    url.searchParams.set("tab", next)
    window.history.replaceState({}, "", url.toString())
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold"><span key={tUi("systemNotifications")}>{tUi("systemNotifications")}</span></h1>
        <p className="text-sm text-muted-foreground">
          <span key={tUi("everythingThePlatformSendsToItsOperatorsAbout")}>{tUi("everythingThePlatformSendsToItsOperatorsAbout")}</span></p>
      </div>
      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList>
          <TabsTrigger value="platform"><span key={tUi("platformEvents")}>{tUi("platformEvents")}</span></TabsTrigger>
          <TabsTrigger value="tenant"><span key={tUi("tenantNotifications")}>{tUi("tenantNotifications")}</span></TabsTrigger>
          <TabsTrigger value="other"><span key={tUi("otherMessages")}>{tUi("otherMessages")}</span></TabsTrigger>
        </TabsList>
        <TabsContent value="platform" className="mt-6">
          <PlatformEventsTab />
        </TabsContent>
        <TabsContent value="tenant" className="mt-6">
          <TenantNotificationsTab />
        </TabsContent>
        <TabsContent value="other" className="mt-6">
          <OtherMessagesTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function parseTab(value: string | null): SystemNotificationsTab {
  return TABS.find((tab) => tab === value) ?? "platform"
}

function PlatformEventsTab() {
  const tUi = useTranslations("SystemNotifications.SystemNotificationsSystemNotificationsPage")

  const { data, isLoading, isError } = useSystemNotificationSettingsQuery()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          <span key={tUi("couldNotLoadSystemNotifications")}>{tUi("couldNotLoadSystemNotifications")}</span></AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          <span key={tUi("eventsAboutTenantsOnThePlatformSentTo")}>{tUi("eventsAboutTenantsOnThePlatformSentTo")}</span></p>
        <TestTenantNotice prefix={data.testTenantPrefix} />
      </div>

      <h2 className="sr-only"><span key={tUi("platformEvents")}>{tUi("platformEvents")}</span></h2>
      <Card className="divide-y">
        {data.events.map((event) => (
          <SystemEventRow
            key={event.eventType}
            event={event}
            channels={data.channels}
            fallbackEmail={data.fallbackEmail}
          />
        ))}
      </Card>
    </div>
  )
}

function Code({ children }: { children: ReactNode }) {
  return <code className="rounded bg-muted px-1 py-0.5 text-xs">{children}</code>
}

/** A bracketed prefix reads with a space, a dash or colon one does not. */
function exampleName(prefix: string, name: string): string {
  return /[A-Za-z0-9\-_:.]$/.test(prefix) ? `${prefix}${name}` : `${prefix} ${name}`
}

/** Whether our own test tenants are reported is invisible from the events themselves. */
function TestTenantNotice({ prefix }: { prefix: string | null }) {
  const tUi = useTranslations("SystemNotifications.SystemNotificationsSystemNotificationsPage")

  if (!prefix) {
    return (
      <p className="text-sm text-muted-foreground">
        <span><span key={tUi("testTenantsAreReportedLikeAnyOtherSet")}>{tUi("testTenantsAreReportedLikeAnyOtherSet")}</span></span>{" "}
        <Code><span key={"PLATFORM_TEST_TENANT_NAME_PREFIX"}>{"PLATFORM_TEST_TENANT_NAME_PREFIX"}</span></Code> <span><span key={tUi("toSkipThem")}>{tUi("toSkipThem")}</span></span>
      </p>
    )
  }

  return (
    <div className="rounded-md border bg-muted/40 px-3 py-2">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="text-muted-foreground"><span key={tUi("tenantsNamedWithThisPrefixAreNeverReported")}>{tUi("tenantsNamedWithThisPrefixAreNeverReported")}</span></span>
        <code className="rounded bg-primary/10 px-2 py-0.5 font-semibold text-foreground">{prefix}</code>
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        <span>e.g.</span> <Code>{exampleName(prefix, "Acme")}</Code>{" "}
        <Code>{exampleName(prefix, "billing check")}</Code> <span><span key={tUi("from")}>{tUi("from")}</span></span>{" "}
        <Code><span key={"PLATFORM_TEST_TENANT_NAME_PREFIX"}>{"PLATFORM_TEST_TENANT_NAME_PREFIX"}</span></Code>
      </p>
    </div>
  )
}
